<template>
  <div class="horizontal-game-lists">
    <!-- Section Title -->
    <div class="section-header">
      <div class="header-content">
        <h3 class="section-title">{{ title }}</h3>
      </div>
    </div>

    <!-- Keen Slider Game Container -->
    <div ref="sliderRef" class="keen-slider games-container">
      <div
        v-for="slide in visibleSlides"
        :key="`slide-${slide.index}`"
        class="keen-slider__slide"
        :class="{ 'slide-placeholder': !slide.isVisible }"
      >
        <!-- Render GameCard only for visible slides -->
        <GameCard
          v-if="slide.isVisible"
          :game="slide.game"
          :show-online-count="showOnlineCount"
          :show-percentage="showPercentage"
          @game-select="selectGame"
          @show-login-modal="$emit('show-login-modal')"
        />
        <!-- Placeholder for non-visible slides to maintain spacing -->
        <div v-else class="slide-placeholder-content">
          <div class="placeholder-shimmer"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import KeenSlider from 'keen-slider'
import GameCard from './GameCard.vue'

// import 'keen-slider/keen-slider.min.css'

export default {
  name: 'HorizontalGameLists',
  components: {
    GameCard
  },
  props: {
    title: {
      type: String,
      required: true
    },
    games: {
      type: Array,
      default: () => []
    },
    showOnlineCount: {
      type: Boolean,
      default: true
    },
    showPercentage: {
      type: Boolean,
      default: false
    },
    autoScroll: {
      type: Boolean,
      default: false
    },
    autoScrollInterval: {
      type: Number,
      default: 3000
    },
    autoScrollDirection: {
      type: String,
      default: 'forward',
      validator: value => ['forward', 'backward'].includes(value)
    },
    pauseOnHover: {
      type: Boolean,
      default: true
    },
    pauseOnTouch: {
      type: Boolean,
      default: true
    },
    // Slider configuration props
    sliderLoop: {
      type: Boolean,
      default: true
    },
    sliderMode: {
      type: String,
      default: 'free-snap',
      validator: value => ['', 'free', 'free-snap', 'snap'].includes(value)
    },
    slidesPerView: {
      type: [String, Number],
      default: 'auto'
    },
    slidesSpacing: {
      type: Number,
      default: 8
    }
  },
  data() {
    return {
      imageLoadedCount: 0,
      slider: null,
      autoplayCleanup: null,
      // Virtual scrolling state
      currentSlideIndex: 0,
      visibleSlideRange: { start: 0, end: 0 },
      slideWidth: 180, // Default slide width
      containerWidth: 0,
      visibleSlidesCount: 0,
      virtualScrollingEnabled: true,
      // IntersectionObserver for deferred initialization
      isInViewport: false,
      intersectionObserver: null
    }
  },
  computed: {
    // Calculate which slides should be rendered
    visibleSlides() {
      if (!this.virtualScrollingEnabled || this.games.length <= 10) {
        // Disable virtual scrolling for small lists
        return this.games.map((game, index) => ({
          game,
          index,
          isVisible: true
        }))
      }

      const buffer = 2 // Render 2 slides before and after visible area
      const { start, end } = this.visibleSlideRange

      return this.games.map((game, index) => {
        const isVisible =
          index >= Math.max(0, start - buffer) &&
          index <= Math.min(this.games.length - 1, end + buffer)
        return { game, index, isVisible }
      })
    },

    // Get responsive slide width based on screen size
    responsiveSlideWidth() {
      if (typeof window === 'undefined') return 180

      const viewportWidth = window.innerWidth
      if (viewportWidth <= 480) return 140
      if (viewportWidth <= 768) return 160
      return 180
    },

    // Lightweight identity for games array to avoid deep watching
    gamesIdentity() {
      if (this.games.length === 0) return 'empty'
      const firstGame = this.games[0]
      return `${this.games.length}-${firstGame?.gameCode ||
        firstGame?.id ||
        ''}`
    }
  },
  mounted() {
    // Use IntersectionObserver to defer slider initialization for below-fold components
    if ('IntersectionObserver' in window) {
      this.intersectionObserver = new IntersectionObserver(
        entries => {
          if (entries[0].isIntersecting && !this.isInViewport) {
            this.isInViewport = true
            this.$nextTick(() => {
              this.initKeenSlider()
            })
            this.intersectionObserver.disconnect()
          }
        },
        {
          rootMargin: '100px 0px', // Start loading 100px before visible
          threshold: 0.1
        }
      )
      this.intersectionObserver.observe(this.$el)
    } else {
      // Fallback for older browsers
      this.$nextTick(() => {
        this.initKeenSlider()
      })
    }
  },
  beforeDestroy() {
    // Clean up IntersectionObserver
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect()
      this.intersectionObserver = null
    }

    // Clean up virtual scrolling
    this.cleanupVirtualScrolling()

    // Clean up autoplay first
    if (this.autoplayCleanup) {
      this.autoplayCleanup()
      this.autoplayCleanup = null
    }

    // Then destroy the slider
    if (this.slider) {
      this.slider.destroy()
      this.slider = null
    }
  },
  watch: {
    // Watch lightweight identity instead of deep watching entire games array
    gamesIdentity() {
      // Only reinitialize if slider is already initialized (in viewport)
      if (this.isInViewport) {
        this.$nextTick(() => {
          this.reinitializeSlider()
        })
      }
    }
  },
  methods: {
    selectGame(game) {
      // Simple pass-through - GameCard now handles auth
      this.$emit('game-select', game)
    },

    // Calculate viewport and visible slide range
    calculateVisibleRange() {
      if (!this.$refs.sliderRef || !this.slider) return

      // Get container dimensions
      this.containerWidth = this.$refs.sliderRef.offsetWidth
      this.slideWidth = this.responsiveSlideWidth + this.slidesSpacing

      // Calculate how many slides are visible
      this.visibleSlidesCount =
        Math.ceil(this.containerWidth / this.slideWidth) + 1

      // Get current slide index from Keen Slider
      const currentIndex = this.slider.track?.details?.rel || 0

      // Calculate visible range
      const start = Math.floor(currentIndex)
      const end = Math.min(
        this.games.length - 1,
        start + this.visibleSlidesCount
      )

      // Update visible range if it changed
      if (
        this.visibleSlideRange.start !== start ||
        this.visibleSlideRange.end !== end
      ) {
        this.visibleSlideRange = { start, end }
        this.currentSlideIndex = currentIndex
      }
    },

    // Initialize virtual scrolling
    initVirtualScrolling() {
      if (!this.virtualScrollingEnabled) return

      // Initial calculation
      this.calculateVisibleRange()

      // Listen for resize events
      window.addEventListener('resize', this.handleResize)
    },

    // Handle window resize
    handleResize() {
      clearTimeout(this.resizeTimeout)
      this.resizeTimeout = setTimeout(() => {
        this.calculateVisibleRange()
      }, 100)
    },

    // Clean up virtual scrolling
    cleanupVirtualScrolling() {
      window.removeEventListener('resize', this.handleResize)
      clearTimeout(this.resizeTimeout)
    },

    // Reinitialize slider when games change
    reinitializeSlider() {
      // Clean up autoplay first
      if (this.autoplayCleanup) {
        this.autoplayCleanup()
        this.autoplayCleanup = null
      }

      // Then destroy and recreate slider
      if (this.slider) {
        this.slider.destroy()
        this.slider = null
      }
      this.initKeenSlider()
    },

    // Keen Slider initialization
    initKeenSlider() {
      if (!this.$refs.sliderRef || !this.games.length) {
        return
      }

      // Check for reduced motion preference
      const prefersReducedMotion = window.matchMedia(
        '(prefers-reduced-motion: reduce)'
      ).matches

      // Configure Keen Slider options using props
      const options = {
        loop: this.sliderLoop,
        mode: this.sliderMode,
        slides: {
          perView: this.slidesPerView,
          spacing: this.slidesSpacing
        }
      }

      // Create plugins array if autoplay is enabled
      const plugins = []

      if (this.autoScroll && !prefersReducedMotion && this.games.length > 1) {
        const {
          autoScrollDirection: direction,
          autoScrollInterval: interval,
          pauseOnHover,
          pauseOnTouch
        } = this

        // Create autoplay plugin following the working example pattern
        const autoplayPlugin = slider => {
          let timeout
          let mouseOver = false

          function clearNextTimeout() {
            clearTimeout(timeout)
          }

          function nextTimeout() {
            clearTimeout(timeout)
            if (mouseOver) return
            timeout = setTimeout(() => {
              if (direction === 'forward') {
                slider.next()
              } else {
                slider.prev()
              }
            }, interval)
          }

          slider.on('created', () => {
            // Basic mouse hover pause (from working example)
            if (pauseOnHover) {
              slider.container.addEventListener('mouseover', () => {
                mouseOver = true
                clearNextTimeout()
              })
              slider.container.addEventListener('mouseout', () => {
                mouseOver = false
                nextTimeout()
              })
            }

            // Optional touch pause functionality
            if (pauseOnTouch) {
              slider.container.addEventListener('touchstart', () => {
                mouseOver = true
                clearNextTimeout()
              })
              slider.container.addEventListener('touchend', () => {
                mouseOver = false
                setTimeout(() => nextTimeout(), 1000)
              })
            }

            nextTimeout()
          })

          slider.on('dragStarted', clearNextTimeout)
          slider.on('animationEnded', nextTimeout)
          slider.on('updated', nextTimeout)

          // Store cleanup function for component destruction
          this.autoplayCleanup = clearNextTimeout
        }

        plugins.push(autoplayPlugin)
      }

      // Initialize the slider with plugins as third parameter (like the working demo)
      try {
        this.slider = new KeenSlider(this.$refs.sliderRef, options, plugins)

        // Initialize virtual scrolling after slider is ready
        this.$nextTick(() => {
          this.initVirtualScrolling()

          // Add slide tracking for virtual scrolling
          this.slider.on('slideChanged', () => {
            this.calculateVisibleRange()
          })

          this.slider.on('updated', () => {
            this.calculateVisibleRange()
          })
        })
      } catch (error) {
        console.error('Keen Slider: Failed to initialize:', error)
      }
    }
  },
  emits: ['game-select', 'show-login-modal']
}
</script>

<style lang="scss" scoped>
.horizontal-game-lists {
  width: 100%;
  margin-bottom: 0px;

  @media (max-width: 768px) {
    margin-bottom: 0px;
  }
}

.section-header {
  padding: 0 20px;
  margin-bottom: 15px;

  @media (max-width: 768px) {
    padding: 0 15px;
    margin-bottom: 12px;
  }
}

.header-content {
  display: flex;
  align-items: center;
  width: 100%;
}

.section-title {
  font-size: clamp(1.5rem, 3vw, 1.8rem);
  color: #fff;
  margin: 0;

  @media (max-width: 480px) {
    font-size: clamp(1.1rem, 3.5vw, 1.3rem);
  }
}

.games-container {
  padding: 0 20px;
  min-height: 280px;

  // Performance optimizations
  will-change: transform;
  transform: translate3d(0, 0, 0);

  // Keen Slider specific styles
  &.keen-slider {
    display: flex;
    overflow: hidden;
    padding: 0 20px;
  }

  @media (max-width: 768px) {
    padding: 0 15px;
    min-height: 250px;

    &.keen-slider {
      padding: 0 15px;
    }
  }

  @media (max-width: 480px) {
    padding: 0 12px;
    min-height: 220px;

    &.keen-slider {
      padding: 0 12px;
    }
  }
}

.keen-slider__slide {
  min-width: 180px;
  width: 180px;
  max-width: 180px;

  @media (max-width: 768px) {
    min-width: 160px;
    width: 160px;
    max-width: 160px;
  }

  @media (max-width: 480px) {
    min-width: 140px;
    width: 140px;
    max-width: 140px;
  }

  // Virtual scrolling placeholder styles
  &.slide-placeholder {
    // Maintain slide dimensions for proper spacing
    flex-shrink: 0;
  }
}

// Placeholder content for virtual scrolling
.slide-placeholder-content {
  width: 100%;
  aspect-ratio: 3/4.1;
  position: relative;
  border-radius: 10px;
  overflow: hidden;
  background: linear-gradient(
    135deg,
    rgba(26, 26, 46, 0.3),
    rgba(22, 33, 62, 0.3)
  );
  display: flex;
  align-items: center;
  justify-content: center;

  .placeholder-shimmer {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.05),
      transparent
    );
    animation: shimmer 3s infinite;
    border-radius: 10px;
  }

  @keyframes shimmer {
    0% {
      transform: translateX(-100%);
    }
    100% {
      transform: translateX(100%);
    }
  }
}

// Game card styles moved to GameCard.vue component

// Responsive adjustments
@media (max-width: 768px) {
  .horizontal-game-lists {
    .section-title {
      font-size: clamp(1.3rem, 4vw, 1.6rem);
    }
  }
}

@media (max-width: 480px) {
  .games-scroll-wrapper {
    padding-bottom: 5px;
  }
}
</style>
